import {
    AITask,
    IActionManager,
    ActionSubscription,
    AIAction,
    ActionType,
    AIResponse
} from '../types/public-types';
import { ILogger } from '~/packages/core';
import { IActionHandler } from '../types/internal-types';
import { uniqueId } from 'lodash';

/**
 * Manages subscriptions to AI actions and routes actions to appropriate subscribers
 * based on feature and action type filters.
 */
export class ActionManager implements IActionManager, IActionHandler {
    /**
     * We use ActionSubscription<any> here to allow storing subscriptions with different
     * type parameters. This is safe because:
     * 1. We only add subscriptions through the type-checked subscribe<T> method
     * 2. When we iterate and call onAction, we've already checked that the action's type
     *    is included in the subscription's actionTypes array
     */
    private subscriptions = new Set<ActionSubscription<any>>();
    private registeredTasks = new Set<string>();
    logger: ILogger;
    // TODO: remove when we're done with the subscription bug
    private readonly uuid: string = uniqueId();

    constructor(logger: ILogger) {
        this.logger = logger;
    }

    /**
     * Subscribe to actions, optionally filtered by feature and action types.
     *
     * @param subscription - Configuration for the action subscription
     * @returns A function that can be called to unsubscribe
     * @public
     */
    public subscribe<T extends ActionType[] = ActionType[]>(
        subscription: ActionSubscription<T>
    ): () => void {
        this.logger.info(
            `[ActionManager-${this.uuid}]: feature ${
                subscription.feature
            } is subscribing to actions: ${
                subscription.actionTypes
            }, timestamp: ${performance.now()}`
        );
        this.subscriptions.add(subscription);
        this.logger.info(
            `[ActionManager-${
                this.uuid
            }]: succesfully subscribed, current subscriptions: ${
                this.subscriptions.size
            }, timestamp: ${performance.now()}`
        );
        return () => {
            this.logger.info(
                `[ActionManager-${this.uuid}]: feature ${
                    subscription.feature
                } is unsubscribing from actions: ${
                    subscription.actionTypes
                }, timestamp: ${performance.now()}`
            );
            this.subscriptions.delete(subscription);
            this.logger.info(
                `[ActionManager-${
                    this.uuid
                }]: succesfully unsubscribed, current subscriptions: ${
                    this.subscriptions.size
                }, timestamp: ${performance.now()}`
            );
        };
    }

    /**
     * Register a task to listen for actions.
     *
     * @param task - The AI task to register
     * @public
     */
    public async registerTask(task: AITask): Promise<void> {
        // Skip if already registered
        if (this.registeredTasks.has(task.id)) {
            return;
        }

        this.logger.info(
            `[ActionManager]: starting to track task ${task.id} for actions`
        );
        this.registeredTasks.add(task.id);

        try {
            for await (const response of task.responseStream()) {
                this.handleResponse(response, task.feature);
            }
        } catch (error) {
            this.logger.error(
                `[ActionManager]: error processing responses for task ${task.id}: ${error}`
            );
        } finally {
            this.registeredTasks.delete(task.id);
        }
    }

    /**
     * Process an action from a task and notify relevant subscribers.
     * This is an internal method used by handleResponse.
     *
     * @param action - The action to process
     * @param feature - The feature that generated the action
     * @private
     */
    private processAction(action: AIAction, feature: string): void {
        this.logger.info(
            `[ActionManager]: processing an action ${action.type} for feature ${feature}, iterating over ${this.subscriptions.size} subscriptions`
        );
        // Notify all relevant subscribers
        this.subscriptions.forEach(subscription => {
            // Skip if feature doesn't match
            if (subscription.feature && subscription.feature !== feature) {
                this.logger.info(
                    `[ActionManager]: skipping action for feature ${feature}, expected ${subscription.feature}`
                );
                return;
            }

            // Skip if action type doesn't match
            if (
                subscription.actionTypes &&
                !subscription.actionTypes.includes(action.type)
            ) {
                this.logger.info(
                    `[ActionManager]: skipping action ${action.type} for feature ${feature}, expected one of ${subscription.actionTypes}`
                );
                return;
            }

            try {
                this.logger.info(
                    `[ActionManager]: notifying subscription for action ${action.type} for feature ${feature}`
                );
                subscription.onAction(action);
            } catch (error) {
                this.logger.error(
                    `[ActionManager]: error notifying subscription for action ${action.type} for feature ${feature}: ${error}`
                );
                subscription.onError?.(error as Error);
            }
        });
    }

    /**
     * Handle a response from any task, processing it if it's an action.
     *
     * @param response - The AI response to handle
     * @param feature - The feature that generated the response
     * @public
     */
    public handleResponse(response: AIResponse, feature: string): void {
        this.logger.info(
            `[ActionManager]: handling task response for feature ${feature}, response id: ${response.uid}`
        );
        if (response.type !== 'action') {
            this.logger.info(
                `[ActionManager]: response ${response.uid} is not an action, skipping`
            );
            return;
        }

        const action = response.content as AIAction;
        this.processAction(action, feature);
    }
}
